import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AuthState,
  User,
  AccountType,
  SignUpConfigResponse,
  FormConfig,
  SignUpByEmailPayload,
  SubAccountType,
  SSOSignUpResponse,
} from './Auth.types';
import { AUTH_LOCAL_STORAGE_KEY, authRoutes, SIGN_IN_PATH, AUTH_LOCAL_COOKIE_KEY } from './constants';
import { removeCookie, setCookie, windowRedirect } from '../../../../util/helpers';
import { SetupMarker } from './accountSetupStorage';

export const initialState: AuthState = {
  isUserAuthenticated: false,
  user: null,
  role: '',
  token: null,
  accountType: AccountType.Investor,
  subAccountType: undefined,
  forms: {
    Issuer: undefined,
    Investor: undefined,
    Realtor: undefined,
  },
  email: '',
  password: '',
  username: '',
  setupMarker: null,
  accountLoadFailed: false,
};
export const AuthReducer = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setToken: (state, action: PayloadAction<{ value: AuthState['token'] }>) => {
      const { value } = action.payload;
      if (typeof value === 'string') {
        state.token = value;
        localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, value);
        setCookie(AUTH_LOCAL_COOKIE_KEY, value);
      }
    },
    setUnauthorizedAccess: (state, action) => {
      state.isUserAuthenticated = false;
      state.token = null;
      state.setupMarker = null;
      state.accountLoadFailed = false;
      localStorage.clear();
      removeCookie(AUTH_LOCAL_COOKIE_KEY);
      if (!authRoutes.includes(window.location.pathname)) {
        windowRedirect(SIGN_IN_PATH);
      }
    },
    setAuthorizedAccess: (state, action: PayloadAction<{ value: any | null }>) => {
      const { value } = action.payload;
      // Save every successful getMe, so a refetch updates the user and its roles.
      if (value.email) {
        state.isUserAuthenticated = true;
        state.user = value;
      }
    },
    setSetupMarker: (state, action: PayloadAction<{ value: SetupMarker | null }>) => {
      state.setupMarker = action.payload.value;
    },
    setAccountLoadFailed: (state, action: PayloadAction<{ value: boolean }>) => {
      state.accountLoadFailed = action.payload.value;
    },

    setRole: (state, action: PayloadAction<{ value: AuthState['role'] }>) => {
      const { value } = action.payload;
      state.role = value;
    },
    setUser: (state, action: PayloadAction<{ value: User | null }>) => {
      const { value } = action.payload;
      state.user = value;
    },
    setAccountType: (state, action: PayloadAction<{ value: AccountType }>) => {
      const { value } = action.payload;
      state.accountType = value;
      //@ts-ignore
      if (state?.forms[value]?.types) {
        //@ts-ignore
        state.subAccountType = state.forms[value].types[0].value;
      } else {
        state.subAccountType = undefined;
      }
    },
    setSubAccountType: (state, action: PayloadAction<{ value: SubAccountType }>) => {
      const { value } = action.payload;
      state.subAccountType = value;
    },
    prepareForm: (state, action: PayloadAction<{ value: SignUpConfigResponse | null }>) => {
      const { value } = action.payload;
      const formConfig: FormConfig = {
        Issuer: undefined,
        Realtor: undefined,
        Investor: undefined,
      };
      value?.accountTypes.map(({ key, types, fields, info }) => {
        switch (key) {
          case 'Issuer':
          case 'Realtor':
            formConfig[key] = {
              key: key,
              info: info,
              fields: fields,
            };
            break;
          case 'Investor':
            formConfig[key] = {
              key: key,
              info: info,
              types: types,
            };
            break;
        }
        return [];
      });
      state.forms = formConfig;
    },
    setSignUpFormPayload: (state, action: PayloadAction<{ value: SignUpByEmailPayload | null }>) => {
      const { value } = action.payload;
      if (value) {
        state.email = value.email;
        state.password = value.password;
        state.username = value.username;
      }
    },
    setForgetPasswordFormPayload: (state, action: PayloadAction<{ value: string | null }>) => {
      const { value } = action.payload;
      if (value) {
        state.email = value;
      }
    },
    setDataFromUserSignUpSSO: (state, action: PayloadAction<{ value: SSOSignUpResponse }>) => {
      const { value } = action.payload;
      if (value) {
        state.email = value.userInfo.email;
      }
    },
  },
});
export const {
  setToken,
  setUser,
  setRole,
  setSignUpFormPayload,
  setSubAccountType,
  setAccountType,
  setForgetPasswordFormPayload,
  setDataFromUserSignUpSSO,
  setSetupMarker,
} = AuthReducer.actions;

export default AuthReducer.reducer;
